/**
 * `apiService` has no timeout: a request on a half-open connection (laptop
 * wakes up, Wi-Fi switched) can hang forever. A provider that waits for it
 * would never refresh again, because every later refresh only marks itself
 * as "run after the current one". With a deadline the hung request counts as
 * failed and the next refresh runs; its late answer is dropped by the
 * provider's generation/in-flight bookkeeping.
 */
export const REQUEST_DEADLINE_MS = 15_000;

/** No answer in time — unlike a refusal, the request may still have been carried out. */
export class RequestDeadlineError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RequestDeadlineError';
  }
}

export function withDeadline<T>(promise: Promise<T>, ms: number = REQUEST_DEADLINE_MS): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new RequestDeadlineError(`Keine Antwort nach ${Math.round(ms / 1000)} s`)), ms);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}
